import { Grid, Typography } from "@mui/material";
import {
  deploymentOptionsAtom,
  envOptionsAtom,
  languageOptionsAtom,
  selectedValuesAtom,
  setAllDefaultsAtom,
  ttsAllDataAtom,
  voiceOptionsAtom,
} from "atoms";
import { useAtom, useAtomValue, useSetAtom } from "jotai";
import { useTranslation } from "react-i18next";
import { getLanguageNames, getLanguages, getVoices } from "shared/utils/tts";
import SettingsSelector from "../components/SettingsSelector";

function TTSSettings(): JSX.Element {
  const { t } = useTranslation();
  const envOptions = useAtomValue(envOptionsAtom);
  const deploymentOptions = useAtomValue(deploymentOptionsAtom);
  const setAllDefaults = useSetAtom(setAllDefaultsAtom);
  const ttsData = useAtomValue(ttsAllDataAtom);
  const [selectedValues, setSelectedValues] = useAtom(selectedValuesAtom);
  const setVoiceOptions = useSetAtom(voiceOptionsAtom);
  const setLanguageOptions = useSetAtom(languageOptionsAtom);

  // On change of environment, update the deployments for that environment
  const onEnvChange = (newEnv: string) => {
    setAllDefaults(newEnv);
  };

  // On change of deployment, update the languages for that deployment
  const onDeploymentChange = (newDeployment: string) => {
    const envData = ttsData.filter(
      (data) => data.envName === selectedValues.selectedEnv
    );
    // update the languages of the selected deployment
    const languages = getLanguages(envData, newDeployment);
    const languageNames = getLanguageNames(languages);
    const selectedLanguage =
      languages.filter((l) => l.name === selectedValues.selectedLanguage)
        .length !== 0
        ? selectedValues.selectedLanguage
        : languageNames[0]?.value;
    // update the voices for the newly updated language
    const voices = getVoices(languages, selectedLanguage);
    const selectedVoice =
      voices.filter((v) => v === selectedValues.selectedVoice).length !== 0
        ? selectedValues.selectedVoice
        : voices[0];

    setVoiceOptions(voices);
    setLanguageOptions(languageNames);
    setSelectedValues({
      ...selectedValues,
      selectedDeployment: newDeployment,
      selectedLanguage,
      selectedVoice,
    });
  };

  const deploymentDesc =
    deploymentOptions.filter(
      (d) => d.name === selectedValues.selectedDeployment
    )[0]?.optionTooltip ||
    t("components.deployment.tooltip", {
      what: "Text-to-Speech",
    });

  return (
    <Grid
      id="tts-global-settings"
      container
      sx={{ padding: "24px 0px 24px 0px" }}
    >
      <Grid item xs={12} md={3} lg={3} sx={{ paddingRight: "16px" }}>
        <SettingsSelector
          id={t("components.env.label")}
          title={t("components.env.label")}
          options={envOptions}
          onChange={onEnvChange}
          selectedValue={selectedValues.selectedEnv}
          shouldShowDropdown
        />
      </Grid>
      <Grid item xs={12} md={3} lg={3} sx={{ paddingRight: "48px" }}>
        <Typography variant="body2" pt={5}>
          {t("components.env.tooltip", { what: "Text-to-Speech" })}
        </Typography>
      </Grid>

      <Grid item xs={12} md={3} lg={3} sx={{ paddingRight: "16px" }}>
        <SettingsSelector
          id={t("components.deployment.label")}
          title={t("components.deployment.label")}
          options={deploymentOptions}
          onChange={onDeploymentChange}
          selectedValue={selectedValues.selectedDeployment}
          shouldShowInlineAlert
          inlineAlertMsg={
            deploymentOptions.length === 0
              ? t("components.deployment.inlineAlertMsg")
              : deploymentOptions[0].name
          }
        />
      </Grid>
      <Grid item xs={12} md={3} lg={3}>
        <Typography variant="body2" pt={5}>
          {deploymentDesc}
        </Typography>
      </Grid>
    </Grid>
  );
}

export default TTSSettings;
